"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.newerVitalCheckInput = exports.newerVitalCheckParamsSchema = exports.actionItemInput = exports.actionItemParamsSchema = exports.oxygenAnsweredSchema = exports.oxygenAnswerInput = exports.workupLocationInput = exports.workupSchema = exports.workupClocksSchema = exports.readingClockSchema = exports.actionItemSchema = exports.oxygenReadingSchema = exports.emailDraftSchema = exports.newerVitalBannerSchema = exports.newerVitalCheckSchema = exports.NEWER_VITAL_CHECK_CODES = exports.NEWER_READING_CHECKED_EMAIL = exports.NEWER_READING_CHECKED_CALL = exports.newerEntrySchema = exports.blockingReasonSchema = void 0;
// The workup contract, as far as the gates built so far fill it (`RPM-022`, `RPM-090`, `J-274`).
//
// `RPM-090`'s API row: "The workup contract returns `blockingReasons: [{ gateId, message, field }]`,
// ordered by the order a nurse can act on them." The location gate is the first gate to report
// through it; `J-223` builds the one blocked surface and folds every other gate into this list.
//
// `RPM-023`'s API row, as `CR-44` left it (`J-534`): "The entry's contract carries a `newerReadings`
// list of the resident's newer entries". A newer entry is its own entry, so nothing joins this one and
// there is no call that would make it.
const zod_1 = require("zod");
const rpm_attempts_js_1 = require("./rpm-attempts.js");
const rpm_enums_js_1 = require("./db/rpm-enums.js");
exports.blockingReasonSchema = zod_1.z
    .object({ gateId: zod_1.z.string(), message: zod_1.z.string(), field: zod_1.z.string() })
    .meta({
    id: "BlockingReason",
    description: "One gate blocking the workup, and the field that lifts it",
});
/** One vital of a newer entry, as it was typed: what she compares against this entry's own. */
const newerEntryReadingSchema = zod_1.z.strictObject({
    vitalReadingId: zod_1.z.uuid(),
    vitalCode: zod_1.z.enum(rpm_enums_js_1.vitalCode.enumValues),
    valueText: zod_1.z.string(),
});
/**
 * One newer entry for the same resident in the same building (`RPM-023`, `CR-44`): another entry,
 * first entered after this one, shown with its values and its time. It is its own entry, with its own
 * classification and its own contacts, and nothing from it joins this one.
 */
exports.newerEntrySchema = zod_1.z
    .object({
    /** The newer entry's current completion record. */
    rpmAlertId: zod_1.z.uuid(),
    /** Its values in force, in the order they were entered. */
    readings: zod_1.z.array(newerEntryReadingSchema),
    /** The one moment the entry was taken, as typed (`J-533`). */
    takenAtLocalDate: zod_1.z.string(),
    takenAtLocalTime: zod_1.z.string(),
    /** When the entry was first entered, which is what makes it newer than this one. */
    enteredAt: zod_1.z.iso.datetime(),
})
    .meta({ id: "NewerEntry", description: "A later entry for the same resident" });
/**
 * The newer-vital confirmation codes, one per gated channel (`RPM-023`, `03-RPM-SCHEMA.md` §7
 * preamble ruling 4): the call's (`J-286`) and the email's (`J-227`). Each is cleared by its own
 * confirmation, and clearing one releases nothing else.
 */
exports.NEWER_READING_CHECKED_CALL = "NEWER_READING_CHECKED_CALL";
exports.NEWER_READING_CHECKED_EMAIL = "NEWER_READING_CHECKED_EMAIL";
exports.NEWER_VITAL_CHECK_CODES = [
    exports.NEWER_READING_CHECKED_CALL,
    exports.NEWER_READING_CHECKED_EMAIL,
];
/**
 * One newer-vital confirmation offered on the entry (`RPM-023`, `J-286`, `J-227`): the code, the
 * channel it holds, and whether it is ticked. Ticked means the code has left
 * `outstanding_gate_codes`; unticked means it is outstanding and the channel is held.
 */
exports.newerVitalCheckSchema = zod_1.z
    .object({
    code: zod_1.z.enum(exports.NEWER_VITAL_CHECK_CODES),
    channel: zod_1.z.enum(["PHONE", "EMAIL"]),
    checked: zod_1.z.boolean(),
})
    .meta({
    id: "NewerVitalCheck",
    description: "One newer-vital confirmation, the channel it holds, and whether it is ticked",
});
/**
 * The newer-vital banner's own label (`RPM-023`, `J-227`): three variants, chosen by the lane and the
 * building. `SUMMARY_ONLY` — a critical at a building whose routing is daily summary only, with that
 * building's daily send times; `NO_CALL` — a critical at a building that takes no call for it, so the
 * email goes at once; `GENERIC` — any other entry the check is asked on, the machine-error entry
 * included, where the deployed tool left the label unset (the block's Blocking row).
 */
exports.newerVitalBannerSchema = zod_1.z
    .object({
    variant: zod_1.z.enum(["SUMMARY_ONLY", "NO_CALL", "GENERIC"]),
    /** The building's daily send times, `HH:MM:SS`, on the `SUMMARY_ONLY` variant; empty otherwise. */
    dailySendTimes: zod_1.z.array(zod_1.z.string()),
})
    .meta({ id: "NewerVitalBanner", description: "Which label the newer-vital banner carries" });
/**
 * The email one routed email step asks her to send (`RPM-037`, `J-227`), derived from the entry and
 * shown read-only. ChartVN never sends it: she copies it into her own mail, sends it, and logs it.
 * Nothing here is stored — the attempt row is the record that an email went (the block's MUST).
 */
exports.emailDraftSchema = zod_1.z
    .object({
    /** The routing step this draft belongs to, in the latest resolution. */
    routingStep: zod_1.z.number().int(),
    /** "Critical Alert" or "Concerning Alert", then the resident and the building. */
    subject: zod_1.z.string(),
    /**
     * The header — resident, building and care unit, when the vitals were taken, one line per
     * abnormal reading with its band and oxygen status — then the facility's content lines and,
     * where the step carries clinical logic, the nurse's note exactly as she typed it.
     */
    body: zod_1.z.string(),
})
    .meta({ id: "EmailDraft", description: "The email a routed email step asks the nurse to send" });
/**
 * One oxygen-saturation reading on the workup, with the oxygen status it carries (`RPM-024`). The
 * control is revealed while the workup holds one, and required while any is `blocking`: scored
 * `GRAY` with nobody asked. `UNKNOWN` was asked, and does not block (`J3-R-322`).
 */
exports.oxygenReadingSchema = zod_1.z
    .object({
    vitalReadingId: zod_1.z.uuid(),
    valueText: zod_1.z.string(),
    oxygenStatus: zod_1.z.enum(rpm_enums_js_1.oxygenStatus.enumValues).nullable(),
    severityBand: zod_1.z.enum(rpm_enums_js_1.severityBand.enumValues).nullable(),
    blocking: zod_1.z.boolean(),
})
    .meta({ id: "OxygenReading", description: "An SpO2 reading and the oxygen status it carries" });
/**
 * One item of the checklist (`RPM-025`, `03-RPM-SCHEMA.md` §4.3.6). Every offered item starts clear,
 * on every lane. `sentence` is the chart sentence this code produces, fixed per code by the block;
 * for `CALLED` it is the latest logged call's, and null until one is logged (`RPM-042`, `J-230`).
 */
exports.actionItemSchema = zod_1.z
    .object({
    actionCode: zod_1.z.enum(rpm_enums_js_1.actionCode.enumValues),
    offered: zod_1.z.boolean(),
    forced: zod_1.z.boolean(),
    checked: zod_1.z.boolean(),
    sentence: zod_1.z.string().nullable(),
})
    .meta({ id: "ActionItem", description: "One checklist item: what was done about the workup" });
/**
 * One reading's two clocks (`RPM-027`): when the vital was taken and when it was entered, as
 * instants with their zone, and each gap named by the two instants it lies between. There is no
 * total: "reporting only the total blames the wrong party".
 */
exports.readingClockSchema = zod_1.z
    .object({
    vitalReadingId: zod_1.z.uuid(),
    vitalCode: zod_1.z.enum(rpm_enums_js_1.vitalCode.enumValues),
    /** Null when the typed moment did not resolve (`RPM-021`). */
    takenAt: zod_1.z.iso.datetime().nullable(),
    takenAtZone: zod_1.z.string().nullable(),
    /** What she typed, so an unresolved moment can still be shown as typed. */
    takenAtLocalDate: zod_1.z.string(),
    takenAtLocalTime: zod_1.z.string(),
    timestampResolution: zod_1.z.enum(rpm_enums_js_1.timestampResolution.enumValues),
    receivedAt: zod_1.z.iso.datetime(),
    /** The building's date the vital was taken on. Null when `takenAt` is. */
    clinicalDay: zod_1.z.string().nullable(),
    /** The building's date it was entered on. */
    entryDay: zod_1.z.string().nullable(),
    /** `received_at` minus `taken_at`: the delay before the vital was entered. Null when unresolved. */
    takenToReceivedSeconds: zod_1.z.number().int().nullable(),
    /** `opened_at` minus `received_at`. Negative for a reading entered after the workup opened. */
    receivedToOpenedSeconds: zod_1.z.number().int(),
})
    .meta({ id: "ReadingClock", description: "When a vital was taken and when it was entered" });
/** The workup's clocks (`RPM-027`): the readings', and the workup's own. */
exports.workupClocksSchema = zod_1.z
    .object({
    /** The building's zone, from the profile the workup pinned; null where it pinned none. */
    zone: zod_1.z.string().nullable(),
    openedAt: zod_1.z.iso.datetime(),
    /** When the lane was settled. Null until something settles it. */
    dispositionAt: zod_1.z.iso.datetime().nullable(),
    readings: zod_1.z.array(exports.readingClockSchema),
    /** Readings left out of any clinical-timing figure because `taken_at` did not resolve. */
    excludedFromClinicalTiming: zod_1.z.number().int(),
})
    .meta({ id: "WorkupClocks", description: "The three clocks of one workup, kept apart" });
/**
 * A reading on this workup that a correction superseded (`RPM-006`, `J-502`): the value she typed,
 * the value that replaced it, and whether the correction joined THIS workup (row 2, before the
 * obligation was fixed) or opened a workup of its own (row 3). The banner names both values.
 */
const supersededReadingSchema = zod_1.z.strictObject({
    vitalCode: zod_1.z.enum(rpm_enums_js_1.vitalCode.enumValues),
    supersededValueText: zod_1.z.string(),
    correctedValueText: zod_1.z.string(),
    inThisWorkup: zod_1.z.boolean(),
});
exports.workupSchema = zod_1.z
    .object({
    rpmAlertId: zod_1.z.uuid(),
    /**
     * Where in the building the resident was when the vital was taken (`RPM-022`, `CR-43`): the care
     * unit's label with the record's room and bed. Null only on an alert with no care unit.
     */
    locationText: zod_1.z.string().nullable(),
    /** The alert's care unit, which the location names and the routing resolves against. */
    careUnitId: zod_1.z.uuid().nullable(),
    locationSource: zod_1.z.enum(rpm_enums_js_1.locationSource.enumValues).nullable(),
    blockingReasons: zod_1.z.array(exports.blockingReasonSchema),
    /**
     * The resident's newer entries at this building, oldest first (`RPM-023`, `CR-44`): the name is
     * the block's API row. Empty where ChartVN holds none, which the banner says in its own words.
     */
    newerReadings: zod_1.z.array(exports.newerEntrySchema),
    /**
     * The newer-vital confirmations offered on this entry, one per gated channel (`RPM-023`,
     * `J-286`, `J-227`). The call's is offered on a critical entry while its call step can still be
     * made; the email's while its email is still to send, and on every machine-error entry.
     */
    newerVitalChecks: zod_1.z.array(exports.newerVitalCheckSchema),
    /** The banner's label while any confirmation is offered; null when none is (`J-227`). */
    newerVitalBanner: exports.newerVitalBannerSchema.nullable(),
    /**
     * The email each routed email step with a recipient asks her to send, in step order (`RPM-037`,
     * `J-227`). Empty on an entry whose routing names no email.
     */
    emailDrafts: zod_1.z.array(exports.emailDraftSchema),
    /** Whether the notification obligation is fixed (`MET` or `UNMET`). */
    obligationFixed: zod_1.z.boolean(),
    /** The workup's SpO₂ readings, oldest first (`RPM-024`). Empty where it holds none. */
    oxygenReadings: zod_1.z.array(exports.oxygenReadingSchema),
    /** The checklist, in the order the codes are declared (`RPM-025`). */
    actionItems: zod_1.z.array(exports.actionItemSchema),
    /** What she typed on this alert that an attempt could go to (`RPM-026`). */
    destinations: zod_1.z.array(rpm_attempts_js_1.destinationSchema),
    /** Every attempt logged on this workup, in order (`RPM-026`). */
    attempts: zod_1.z.array(rpm_attempts_js_1.attemptSchema),
    /** Reading time, entry time and workup time, never added into one figure (`RPM-027`). */
    clocks: exports.workupClocksSchema,
    /**
     * Where the workup is in its lifecycle (`SM-ALERT`), and whether a notification is still owed
     * (`RPM-089`, `J-279`): together with `blockingReasons`, what decides whether it may be completed.
     */
    alertState: zod_1.z.enum(rpm_enums_js_1.alertState.enumValues),
    notificationObligationStatus: zod_1.z.enum(rpm_enums_js_1.obligationStatus.enumValues),
    /**
     * The lane the machine computed and the lane that stands, and whether a nurse's override is what
     * holds it there (`RPM-017`, `J-499`): the Lane control shows both and the suggestion line names
     * them where they differ. `auto_lane` may be `GRAY`; `lane` never is (`RPM-015`).
     */
    autoLane: zod_1.z.enum(rpm_enums_js_1.autoLane.enumValues).nullable(),
    lane: zod_1.z.enum(rpm_enums_js_1.alertLane.enumValues).nullable(),
    laneOverridden: zod_1.z.boolean(),
    /** Every reading on this workup a correction superseded, oldest first (`RPM-006`). */
    supersededReadings: zod_1.z.array(supersededReadingSchema),
    /**
     * A correction joined this workup after its lane was last resolved: the lane was computed from
     * the value before it, and the nurse re-resolves it from the banner (`RPM-006` row 2).
     */
    reresolvePending: zod_1.z.boolean(),
})
    .meta({
    id: "Workup",
    description: "One workup: its location, what blocks it, and newer entries",
});
/**
 * The care unit the nurse gives the alert (`RPM-022`, `CR-43`). The server writes the location from
 * it, marks it `NURSE_ENTERED` and re-resolves the routing; the text itself is never sent.
 */
exports.workupLocationInput = zod_1.z
    .strictObject({ careUnitId: zod_1.z.uuid() })
    .meta({ description: "The care unit the resident was on, as the nurse chose it" });
/**
 * The nurse's answer (`RPM-024`). Two values, the two the control offers: unanswered is the absence
 * of a choice and never a value, so there is nothing to send for it (`D-O2-01`).
 */
exports.oxygenAnswerInput = zod_1.z
    .strictObject({ oxygenStatus: zod_1.z.enum(["ROOM_AIR", "ON_OXYGEN"]) })
    .meta({ description: "The oxygen status the nurse chose for the workup's SpO2 readings" });
/**
 * What the answer did. The re-scored lane is here, so the client never recomputes it (`RPM-024`'s
 * API row); the workup carries the re-scored readings and whatever still blocks.
 */
exports.oxygenAnsweredSchema = zod_1.z
    .object({
    lane: zod_1.z.enum(rpm_enums_js_1.alertLane.enumValues).nullable(),
    laneOverridden: zod_1.z.boolean(),
    workup: exports.workupSchema,
})
    .meta({
    id: "OxygenAnswered",
    description: "The workup re-scored under the oxygen status chosen",
});
exports.actionItemParamsSchema = zod_1.z.strictObject({
    alertId: zod_1.z.uuid(),
    actionCode: zod_1.z.enum(rpm_enums_js_1.actionCode.enumValues),
});
/** Tick or untick one item. The code is the path; nothing else is sent, and nothing is free text. */
exports.actionItemInput = zod_1.z
    .strictObject({ checked: zod_1.z.boolean() })
    .meta({ description: "Whether this checklist item is ticked" });
/** `PUT /api/rpm/v1/alerts/:alertId/newer-vital-checks/:code` (`RPM-023`, `J-286`). */
exports.newerVitalCheckParamsSchema = zod_1.z.strictObject({
    alertId: zod_1.z.uuid(),
    code: zod_1.z.enum(exports.NEWER_VITAL_CHECK_CODES),
});
/**
 * Tick or untick one confirmation: "confirming the check is an explicit call per code" (`RPM-023`'s
 * API row). The code is the path; the body is one boolean.
 */
exports.newerVitalCheckInput = zod_1.z
    .strictObject({ checked: zod_1.z.boolean() })
    .meta({ description: "Whether this newer-vital confirmation is ticked" });
//# sourceMappingURL=rpm-workup.js.map